import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import contactStyles from "../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/ticketStyles/ticket.module.scss";
import { Autocomplete, Chip, Divider, Grid, TextField, Typography } from '@mui/material';
import Required from '../../../../../components/utility/required';
import { useTicketStore } from '../../../../../store/ticketStore';
import { useShallow } from 'zustand/react/shallow';
import { createTicketPayload, organizationUserDataModel, ticketFieldData, ticketStatusData, updateFieldDataPayload, updateStatusAssignmentPayload, updateTicketPayload } from '../../../../../models/axiosModel';
import { useSearchParams } from 'react-router-dom';
import IconsColor from '../../../../../components/utility/iconsColor';
import { lightGreyColor, primaryColor } from '../../../../../utils/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import GlobalButton from '../../../../../components/utility/globalButton/button';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import agent from '../../../../../services/api';
import { filterAutocompleteOptionsWithException, getUserFullName, hasTicketSubstatusManageAccess } from '../../../../../utils/constants';
import { THREAD_STATUS } from '../threadStatus/customerConversationAssignedStatus';
import RenderCustomOption from '../../../../../components/global/autocomplete/renderCustomOption';
import { getTicketSubStatusObj } from '../../../../../components/dashboard/organization/ticketSubStatus/constants';
import GlobalTooltip from '../../../../../components/utility/tooltip/globalTooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { useGenericStore } from '../../../../../store/genericStore';
import { formatPhoneNumber, getMetadataValue, getnewEmptyAgentAssignmentObj, getNewEmptyField, getNewEmptyStatusObj, getTicketPayload, getUserAgentOptions } from '../../../utils';
import { renderTicketStatusOption } from '../../../renderUtils';
import clsx from "clsx";
import { METADATA_KEYS } from '../../../constants';

type fieldValuesType =
    | { type: 'field value', field: updateFieldDataPayload }
    | { type: 'status' }
    | { type: 'substatus' }
    | { type: 'assign to' }

type updateFieldValueType =
    | { type: 'field value', field: updateFieldDataPayload, value: string }
    | { type: 'add field', field: ticketFieldData }
    | { type: 'remove field', field: updateFieldDataPayload }
    | { type: 'status', value: ticketStatusData | null }
    | { type: 'substatus', value: ticketStatusData[] | null }
    | { type: 'assign to', value: organizationUserDataModel[] }

const CreateTicketPopup: React.FC = () => {

    const innerHeight = window.innerHeight - 325;
    const [searchParams] = useSearchParams()
    const chatbotUserId = searchParams.get('chatbot_user_id');

    const { ticketFieldData, selectedTicket, filterStatusesBySubstatusFlag, ticketStatusData, setSelectedTicket, getTicketStatusListApi, setFlagCreateTicketPopup } = useTicketStore(
        useShallow((state) => ({
            ticketFieldData: state.ticketFieldData,
            selectedTicket: state.selectedTicket,
            filterStatusesBySubstatusFlag: state.filterStatusesBySubstatusFlag,
            ticketStatusData: state.ticketStatusData,
            setSelectedTicket: state.setSelectedTicket,
            getTicketStatusListApi: state.getTicketStatusListApi,
            setFlagCreateTicketPopup: state.setFlagCreateTicketPopup
        }))
    )

    const { updateThreadMetadata, currentActiveChatbotThread, setCurrentActiveChatbotThread } = useRuleBasedInboxStore(
        useShallow((state) => ({
            updateThreadMetadata: state.updateThreadMetadata,
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread
        }))
    )

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const { userDetail, organizationDetails, setFlagLoading, handleError, getUserAssignmentRoleApi, userAssignmentRoleData } = useContext(UserContext) as UserContextPayload;

    const [payload, setPayload] = useState<updateTicketPayload>(getTicketPayload(chatbotUserId, ticketFieldData, selectedTicket, ticketStatusData) as updateTicketPayload)

    const getUserAssignmentRole = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
        const res = await getUserAssignmentRoleApi();
        if(!(res.status)){
            handleError(res);
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }))    
    } 

    const getTicketStatusApiCall = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await getTicketStatusListApi();
        if(!(res.status)){
            handleError(res);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    useEffect(() => {
        if(!userAssignmentRoleData){
            getUserAssignmentRole();
        }
    }, [])

    const getSortedTicketFields = (fieldData: ticketFieldData[]) => {
        if (fieldData?.length > 0) {
            return fieldData?.sort((a: ticketFieldData, b: ticketFieldData) => {
                const sequenceA = a?.sequence;
                const sequenceB = b?.sequence;
                return sequenceA - sequenceB;
            })
        }
        return [];
    }

    const filterFieldsBasedOnFlagRequired = (flagRequired: boolean) => {
        if(ticketFieldData && ticketFieldData?.length > 0){
            return ticketFieldData?.filter((field) => field?.flag_required === flagRequired);
        }
        return [];
    } 

    const requiredTicketFields = useMemo(() => getSortedTicketFields(filterFieldsBasedOnFlagRequired(true)), [ticketFieldData]);
    const optionalTicketFields = useMemo(() => getSortedTicketFields(filterFieldsBasedOnFlagRequired(false)),[ticketFieldData]);

    const findStatusAssignment = (statusAssignment: updateStatusAssignmentPayload[], flagSubStatus: boolean) => {
        return statusAssignment?.find((assignedStatus) => ticketStatusData?.find((status) => assignedStatus?.ticket_status_id === status?.ticket_status_id && status?.flag_substatus === flagSubStatus));
    }

    const filterStatusAssignment = (statusAssignment: updateStatusAssignmentPayload[], flagSubStatus: boolean) => {
        return statusAssignment?.filter((assignedStatus) => ticketStatusData?.find((status) => assignedStatus?.ticket_status_id === status?.ticket_status_id && status?.flag_substatus === flagSubStatus));
    }

    const getFieldName = (thread_ticket_field_id: String) => {
        return ticketFieldData?.find((field) => field?.thread_ticket_field_id === thread_ticket_field_id)?.thread_ticket_field_name ?? '';
    }

    const getFieldValues = (params: fieldValuesType) => {
        switch (params.type) {
            case 'field value':
                const field = params?.field;
                const fieldData = payload?.field_data?.find((ticketField) => ticketField?.thread_ticket_field_data_id === field?.thread_ticket_field_data_id);
                return fieldData?.field_value ?? '';
            case 'status':
                const statusObj = findStatusAssignment(payload?.status_assignments ?? [], false);
                return ticketStatusData?.find((status) => status?.ticket_status_id == statusObj?.ticket_status_id) ?? null;
            case 'substatus':
                const subStatuses = filterStatusAssignment(payload?.status_assignments ?? [], true);
                return ticketStatusData?.filter((status) => subStatuses?.some((subStatus) => subStatus?.ticket_status_id === status?.ticket_status_id)) ?? [];
            case 'assign to':
                return organizationDetails?.[0]?.organization_user_data?.filter((user) => payload?.agent_assignments?.some((agent) => agent?.agent_user_assignment_id === user?.user_assignment_id)) ?? [];
            default:
                return '';
        }
    }

    const onChangeOfFields = (params: updateFieldValueType) => {
        setPayload((prev) => {
            if (!prev) return prev;
            let updatedPayload = prev;
            switch (params?.type) {
                case 'field value':
                    return {
                        ...updatedPayload,
                        field_data: updatedPayload?.field_data?.map((ticketField) => {
                            if (ticketField?.thread_ticket_field_data_id === params?.field?.thread_ticket_field_data_id) {
                                return {
                                    ...ticketField,
                                    field_value: params?.value
                                }
                            }
                            return ticketField;
                        })
                    }
                case 'add field':
                    return {
                        ...updatedPayload,
                        field_data: [...updatedPayload?.field_data, getNewEmptyField(params?.field)]
                    }
                case 'remove field':
                    return {
                        ...updatedPayload,
                        field_data: updatedPayload?.field_data?.filter((ticketField) => ticketField?.thread_ticket_field_data_id !== params?.field?.thread_ticket_field_data_id)
                    }
                case 'status':
                    return {
                        ...updatedPayload,
                        status_assignments: params?.value
                            ? [...filterStatusAssignment(updatedPayload?.status_assignments, true), getNewEmptyStatusObj(params?.value)]
                            : filterStatusAssignment(updatedPayload?.status_assignments, true)
                    }
                case 'substatus':
                    return {
                        ...updatedPayload,
                        status_assignments: params?.value
                            ? [...filterStatusAssignment(updatedPayload?.status_assignments, false),
                            ...params?.value?.map((subStatus) => {
                                return getNewEmptyStatusObj(subStatus);
                            })
                            ]
                            : filterStatusAssignment(updatedPayload?.status_assignments, false)
                    }
                case 'assign to':
                    return {
                        ...updatedPayload,
                        agent_assignments: params?.value?.map((user) => {
                            return getnewEmptyAgentAssignmentObj(user);
                        })
                    }
                default:
                    return prev;
            }
        })
    }

    const renderTicketFields = (field: ticketFieldData) => {
        const dataType = field?.data_type_name?.toLowerCase();
        const fieldData = payload?.field_data?.filter((ticketField) => ticketField?.thread_ticket_field_id === field?.thread_ticket_field_id) ?? [];  
        const flagMultipleFields = fieldData?.length > 1 && field?.flag_allowed_multiple

        return fieldData?.map((field: updateFieldDataPayload) => {  
            switch (dataType) {
                case 'string':
                    return (
                        <TextField
                            value={getFieldValues({ type: 'field value', field: field }) as string}
                            onChange={(e) => onChangeOfFields({ type: 'field value', field: field, value: e.target.value })}
                            className={clsx(contactStyles.campaignContactPopupTextField, globalStyles.fullwidth)}
                            placeholder={`Enter ${getFieldName(field?.thread_ticket_field_id)}`}
                        />
                    );
                case 'text':
                    return (
                        <Grid container className={clsx(globalStyles.displayFlex, flagMultipleFields && styles.textAreaWrapper)} gap={1} flexWrap={"nowrap"} mb={flagMultipleFields ? 1 : 0}>
                            <Grid item xs={12} className={flagMultipleFields ? styles.textAreaWithDelete : ''}>
                                <textarea
                                    rows={1}
                                    placeholder={`Enter ${getFieldName(field?.thread_ticket_field_id)}`}
                                    className={clsx(contactStyles.textareaStyle, globalStyles.fullwidth)}
                                    value={getFieldValues({ type: 'field value', field: field }) as string}
                                    onChange={(e) => onChangeOfFields({ type: 'field value', field: field, value: e.target.value })}
                                />
                                {flagMultipleFields && (
                                    <span className={styles.deleteIconWrapper} onClick={() => onChangeOfFields({ type: 'remove field', field: field })}>
                                        <DeleteIcon  className={styles.deleteFieldIcon} />
                                    </span>
                                )}
                            </Grid>
                        </Grid>
                    );
                default:
                    return <></>;
            }
        })
    }

    const renderSubstatusChip = (option: ticketStatusData, chipProps?: React.ComponentProps<typeof Chip> & { isOption?: boolean }) => {
        const { isOption, ...restChipProps } = chipProps || {};
        const chipColor = option?.ticket_status_color || lightGreyColor;
        return (
            <Chip
                label={option?.ticket_status_name}
                size="small"
                className={isOption ? styles.substatusOptionChip : undefined}
                sx={{ '--chip-color': chipColor, '--chip-bg': `${chipColor}` }}
                {...restChipProps}
            />
        );
    };

    const renderSubstatusOption = (option: ticketStatusData) => (
        renderSubstatusChip(option, { isOption: true })
    );

    const renderSubstatusTags = (value: ticketStatusData[], getTagProps: (params: { index: number }) => object) =>
        value.map((option, index) => renderSubstatusChip(option, { key: option?.ticket_status_id, ...getTagProps({ index }) }));

    const renderTicketFieldBlock = (fieldData: ticketFieldData[]) => {
        return fieldData?.map((field) => {
            return (
                <Grid item xs={12} key={field?.thread_ticket_field_id}>
                    <Grid container gap={"12px"}>
                        <Grid item xs={12}>
                            <Grid container className={clsx(globalStyles.displayFlex, globalStyles.justifyContentSpaceBetween)}>
                                <Grid item>
                                    <Typography className={contactStyles.popupFieldTypography}>
                                        {field?.thread_ticket_field_name} {field?.flag_required && <Required />}
                                    </Typography>
                                </Grid>
                                {field?.flag_allowed_multiple &&
                                    <Grid item>
                                        <span
                                            className={clsx(globalStyles.primaryColor, globalStyles.displayFlex, globalStyles.clickable, globalStyles.gapOne, globalStyles.secondaryFontSize, globalStyles.primaryFontWeight)}
                                            onClick={() => onChangeOfFields({ type: 'add field', field: field })}
                                        >
                                            <IconsColor iconName='plus' width="15" height="15" fillColor={primaryColor} />
                                            Add
                                        </span>
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            {renderTicketFields(field)}
                        </Grid>
                    </Grid>
                </Grid>
            );
        })
    }

    const isSaveBtnDisabled = useCallback(() => {
        if (!payload) return true;

        // Validate all required custom fields
        const isAnyRequiredFieldEmpty = requiredTicketFields?.some((field) => {
            const relatedFieldData = payload.field_data?.filter((ticketField) => ticketField?.thread_ticket_field_id === field?.thread_ticket_field_id) ?? [];

            // If there is no field_data entry for a required field or any value is empty, disable Save
            if (relatedFieldData.length === 0) return true;

            return relatedFieldData.some((ticketField) => ticketField?.field_value?.trim() === "");
        });

        if (isAnyRequiredFieldEmpty) return true;

        // Validate Ticket Status – at least one status with flag_substatus === false
        const primaryStatus = findStatusAssignment(payload.status_assignments ?? [], false);
        if (!primaryStatus) return true;

        return false;
    }, [payload, ticketFieldData, ticketStatusData])
    
    const onClickOfCancelBtn = () => {
        setFlagCreateTicketPopup(false);
        setSelectedTicket(null);
    }

    const onClickOfSaveTicket = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        let res;
        if(selectedTicket){
            const updatePayload: updateTicketPayload = {
                thread_ticket_id: selectedTicket?.thread_ticket_id,
                thread_id: currentActiveChatbotThread?.chatbot_user_id ?? '',
                field_data: payload?.field_data?.filter((field) => field?.field_value?.length)?.map((field) => {
                    return {
                        ...field,
                        thread_ticket_field_data_id: selectedTicket?.field_data?.find((fieldData) => fieldData?.thread_ticket_field_data_id === field?.thread_ticket_field_data_id)?.thread_ticket_field_data_id ?? ''
                    }
                }) ?? [],
                agent_assignments: payload?.agent_assignments?.map((agent) => {
                    return {
                        ...agent,
                        thread_ticket_agent_assignment_id: selectedTicket?.agent_assignments?.find((assignedAgent) => assignedAgent?.agent_user_assignment_id === agent?.agent_user_assignment_id)?.thread_ticket_agent_assignment_id ?? ''
                    }
                }) ?? [],
                status_assignments: payload?.status_assignments?.map((status) => {
                    return {
                        ...status,
                        thread_ticket_status_assignment_id: selectedTicket?.status_assignments?.find((assignedStatus) => assignedStatus?.ticket_status_id === status?.ticket_status_id)?.thread_ticket_status_assignment_id ?? ''
                    }
                }) ?? []
            }
            res = await agent.updateThreadTicket.put(updatePayload);
        } else {
            const createTicketPayload: createTicketPayload = {
                thread_id: currentActiveChatbotThread?.chatbot_user_id ?? '',
                field_data: payload?.field_data?.filter((field) => field?.field_value?.length)?.map((field) => {
                    return {
                        thread_ticket_field_id: field?.thread_ticket_field_id,
                        field_value: field?.field_value
                    }
                }) ?? [],
                agent_assignments: payload?.agent_assignments?.map((agent) => {
                    return {
                        agent_user_assignment_id: agent?.agent_user_assignment_id
                    }
                }) ?? [],
                status_assignments: payload?.status_assignments?.map((status) => {
                    return {
                        ticket_status_id: status?.ticket_status_id
                    }
                }) ?? []
            }
            res = await agent.createThreadTicket.post(createTicketPayload);
        }

        if(!(res.status)) {
            handleError(res);
        } else {
            const successMsg = `Ticket ${selectedTicket ? 'Edited' : 'Created'} successfully`
            showSuccess(successMsg)
            
            if(currentActiveChatbotThread && currentActiveChatbotThread?.thread_id === res?.data?.thread_id){
                let updatedCurrentThread = currentActiveChatbotThread;
                updatedCurrentThread = {
                    ...updatedCurrentThread,
                    thread_ticket_data: updatedCurrentThread?.thread_ticket_data?.find((ticket) => ticket?.thread_ticket_id === res?.data?.thread_ticket_id) ? 
                                        updatedCurrentThread?.thread_ticket_data?.map((ticket) => {
                                            if(ticket?.thread_ticket_id === res?.data?.thread_ticket_id){
                                                return res?.data;
                                            }
                                            return ticket;
                                        }) 
                                        : [...updatedCurrentThread?.thread_ticket_data, res?.data] 
                }
                setCurrentActiveChatbotThread(updatedCurrentThread);

                updateThreadMetadata(updatedCurrentThread?.chatbot_user_id, {
                    thread_ticket_data: updatedCurrentThread?.thread_ticket_data,
                });
            }
        }
        onClickOfCancelBtn();
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));

    }

    const getPopupTitle = useCallback(() => {
        let title = 'Create Ticket';
        if(selectedTicket){
            title = `Edit Ticket - ${selectedTicket?.ticket_reference_number}`;
        }
        return title;
    }, [selectedTicket])

    const getAgentOptions = useCallback(() => {
        return getUserAgentOptions(organizationDetails, userAssignmentRoleData);
    }, [organizationDetails, userAssignmentRoleData])

    const getTicketSubStatusOptions = useCallback(() => {
        const options: ticketStatusData[] = filterStatusesBySubstatusFlag(true);
        if(hasTicketSubstatusManageAccess(userDetail)){
            const customTicketSubStatusObj = getTicketSubStatusObj("-1", "New Substatus");
            options.push(customTicketSubStatusObj);
        }
        return options;
    }, [filterStatusesBySubstatusFlag, userDetail, ticketStatusData]);

    return (
        <>
            <Grid container >
                <Grid item xs={12}>
                    <Grid container direction={"column"} justifyContent={"center"}>
                        <Grid item className={styles.ticketPopupTitle}>
                            <Grid container gap={1} alignItems={"center"} className={`${globalStyles.displayFlexStyle}`}>
                                <Grid item>
                                    <ConfirmationNumberOutlinedIcon className={styles.ticketIcon} />
                                </Grid>
                                <Grid item>
                                    <Typography className={`${styles.ticketTitleText}`}>
                                        {getPopupTitle()}
                                    </Typography>
                                </Grid>  
                            </Grid>
                        </Grid>

                        <Divider className={contactStyles.popupTitleDivider} />

                        <Grid item className={`${globalStyles.fullwidth}`}>
                            <Grid container className={styles.customerInfoGrid}>
                                <Grid item xs={12} className={`${styles.customerInfo}`}>
                                    <Grid container gap={1}>
                                        <Grid item xs={12}>
                                            <Typography className={styles.customerTitle}>
                                                Customer
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography className={contactStyles.popupFieldTypography}>
                                                {currentActiveChatbotThread ? 
                                                    `${getMetadataValue(currentActiveChatbotThread?.thread_metadata, METADATA_KEYS['NAME']) ?? "Unknown"} ${getMetadataValue(currentActiveChatbotThread?.thread_metadata, METADATA_KEYS['PHONE_NUMBER']) ? `(${formatPhoneNumber(getMetadataValue(currentActiveChatbotThread?.thread_metadata, METADATA_KEYS['PHONE_NUMBER']) ?? '')})` : ''} `
                                                    : ''
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item className={clsx(globalStyles.fullwidth, globalStyles.overflowAuto)} sx={{ maxHeight: `${innerHeight}px !important` }}>
                            <Grid container className={contactStyles.popupBodyGridContainer} gap={2}>

                                {renderTicketFieldBlock(requiredTicketFields)}

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={contactStyles.popupFieldTypography}>
                                                Ticket Status <Required />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                className={`${contactStyles.contactFilterDropdownsInPopup}`}
                                                options={filterStatusesBySubstatusFlag(false)}
                                                getOptionLabel={(option) => option?.ticket_status_name ?? ''}
                                                value={getFieldValues({ type: 'status' }) as ticketStatusData | null}
                                                isOptionEqualToValue={(option, value) =>
                                                    option.ticket_status_id === value.ticket_status_id
                                                }
                                                onChange={(_, value) => onChangeOfFields({ type: 'status', value: value })}
                                                renderInput={(params) => <TextField {...params} placeholder="Select Ticket Status" />}
                                                renderOption={(props, option) => renderTicketStatusOption(props, option)}
                                                getOptionDisabled={(option) => !selectedTicket && option?.ticket_status_name === THREAD_STATUS['RESOLVED']}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Grid container alignItems={'center'} justifyContent={'space-between'}>
                                                <Typography className={contactStyles.popupFieldTypography}>
                                                    Ticket Substatus
                                                </Typography>
                                                <GlobalTooltip
                                                    title="Fetch latest substatus"
                                                    placement='top'
                                                    arrow
                                                >
                                                    <RefreshIcon className={styles.subStatusRefreshIcon} onClick={() => getTicketStatusApiCall()} />
                                                </GlobalTooltip>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                multiple
                                                className={clsx(contactStyles.contactFilterDropdownsInPopup, styles.ticketSubstatusAutocomplete)}
                                                options={getTicketSubStatusOptions()}
                                                getOptionLabel={(option) => option?.ticket_status_name ?? ''}
                                                value={getFieldValues({ type: 'substatus' }) as ticketStatusData[]}
                                                isOptionEqualToValue={(option, value) =>
                                                    option.ticket_status_id === value.ticket_status_id
                                                }
                                                filterOptions={(options, state) => filterAutocompleteOptionsWithException(options, state, "ticket_status_name", "ticket_status_id")}
                                                onChange={(_, value) => onChangeOfFields({ type: 'substatus', value: value })}
                                                renderInput={(params) => <TextField {...params} placeholder="Select Ticket Substatus" />}
                                                ListboxProps={{ className: contactStyles.singleContactListbox }}
                                                renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ticketStatusData) => {
                                                    return (
                                                        <RenderCustomOption
                                                            autocompleteRenderOptionProps={props} 
                                                            option={option} 
                                                            customOptionName={"New Substatus"}
                                                            optionName={option['ticket_status_name']}
                                                            optionLabelKey={'ticket_status_id'}
                                                            onClickOfCustomOption={() => window.open(`/organization/ticketSubstatus?organization_id=${organizationDetails?.[0]?.organization_id}`)}
                                                            flagRenderCustomOption
                                                            renderCustomOption={(option) => renderSubstatusOption(option)}
                                                        />
                                                    );
                                                }}
                                                renderTags={(value, getTagProps) => renderSubstatusTags(value, getTagProps)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={contactStyles.popupFieldTypography}>
                                                Assign to
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                multiple
                                                className={clsx(contactStyles.contactFilterDropdownsInPopup, styles.assignToAutoComplete)}
                                                options={getAgentOptions() ?? []}
                                                getOptionLabel={(option) => getUserFullName(option)}
                                                value={getFieldValues({ type: 'assign to' }) as organizationUserDataModel[]}
                                                isOptionEqualToValue={(option, value) => option?.user_assignment_id === value?.user_assignment_id}
                                                onChange={(_, value) => onChangeOfFields({ type: 'assign to', value: value })}
                                                renderInput={(params) => <TextField {...params} placeholder="Assign to" />}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {renderTicketFieldBlock(optionalTicketFields)}
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container justifyContent={"center"} alignItems={"Center"} gap={"12px"} padding={"10px 0px 25px"}>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Cancel"
                                        className="borderedButtonStyle"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        onClick={() => onClickOfCancelBtn()}
                                    />
                                </Grid>
                                <Grid item>
                                    <GlobalButton
                                        buttonText={selectedTicket ? 'Save' : 'Create'}
                                        className='primaryButtonStyle'
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        disabled={isSaveBtnDisabled()}
                                        onClick={() => onClickOfSaveTicket()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default CreateTicketPopup
